import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { AnalyticPanelContext } from 'utils/context/AnalyticPanelContext';
import { v4 } from 'uuid';

import AnalyticPanelPdfPages from 'components/analyticPanel/AnalyticPanelReport/AnalyticPanelPdfPages';
import AnalyticPanelReport, { hasReportRequiredData } from 'components/analyticPanel/AnalyticPanelReport/AnalyticPanelReport';
import FiltersAnalyticPanel from 'components/analyticPanel/FiltersAnalyticPanel';
import Indicator from 'components/atom/Indicator';
import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import { activeNotification } from 'components/functions/activeNotification';
import V2HeaderSection from 'components/V2HeaderSection';

const Container = styled.div`
    padding: 15px 20px 0;
`;

const AddOfferButton = styled.div`
    cursor: pointer;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    margin: 10px 0 0 15px;
    display: flex;
    align-items: center;
    font-size: 15px;
    height: 50px;
    border: 2px solid var(--color-primary);
    border-radius: 10px;
    padding: 12px;

    @media (max-width: 768px) {
        margin: -25px 0 10px 0;
        height: auto;
        align-self: flex-start;
    }
`;

const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;
const PDF_CANVAS_SCALE = 1.5;
const PDF_JPEG_QUALITY = 0.9;
// Czas na wyrenderowanie stron poza ekranem i pomiar wykresów (ResponsiveContainer); animacje w PDF są wyłączone
const PDF_RENDER_DELAY_MS = 1000;

const AnalyticPanelPage = () => {
    const {
        filtersAnalyticPanelState,
        showReport,
        setShowReport,
        clearFilter
    } = useContext(AnalyticPanelContext);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const pdfFirstPageRef = useRef<HTMLDivElement>(null);
    const pdfSecondPageRef = useRef<HTMLDivElement>(null);
    const pdfTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (pdfTimeoutRef.current) clearTimeout(pdfTimeoutRef.current);
    }, []);

    const canDownloadPdf = hasReportRequiredData(filtersAnalyticPanelState);

    const handleDownloadPdf = () => {
        setIsExportingPdf(true);

        pdfTimeoutRef.current = setTimeout(async () => {
            const firstPage = pdfFirstPageRef.current;
            const secondPage = pdfSecondPageRef.current;

            if (!firstPage || !secondPage) {
                setIsExportingPdf(false);

                return;
            }

            try {
                const canvases = await Promise.all([
                    html2canvas(firstPage, { scale: PDF_CANVAS_SCALE }),
                    html2canvas(secondPage, { scale: PDF_CANVAS_SCALE })
                ]);
                const pdf = new jsPDF('p', 'pt', 'a4');

                canvases.forEach((canvas, index) => {
                    if (index > 0) pdf.addPage();
                    pdf.addImage(canvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY), 'JPEG', 0, 0, A4_WIDTH_PT, A4_HEIGHT_PT);
                });

                pdf.save(`raport_inwestycyjny_investoro_${dayjs().format('DD_MM_YYYY')}.pdf`);
            } catch (error) {
                const errorId = v4();
                userApiRequests.logException(error as Error, errorId);
                activeNotification('Błąd generowania PDF', 'Nie udało się wygenerować pliku PDF. Spróbuj ponownie.', 'warning');
            } finally {
                setIsExportingPdf(false);
            }
        }, PDF_RENDER_DELAY_MS);
    };

    const handleBackToForm = () => {
        setShowReport(false);
    };

    const handleNewAnalysis = () => {
        clearFilter();
        setShowReport(false);
    };

    const newAnalysisButton = <AddOfferButton onClick={handleNewAnalysis}>
        + Nowa analiza
    </AddOfferButton>;

    if (showReport) {
        return (
            <div>
                <V2HeaderSection
                    title={'Panel analityczny - Raport'}
                    handleBackToFilters={handleBackToForm}
                    customStepBackText={'Powrót do formularza'}
                    additionalElement={newAnalysisButton}
                    handlePDFClick={canDownloadPdf && !isExportingPdf ? handleDownloadPdf : undefined}
                    showPDFOnSmallScreens
                />
                <AnalyticPanelReport state={filtersAnalyticPanelState} />
                {isExportingPdf &&
                    <>
                        <Indicator />
                        <AnalyticPanelPdfPages
                            data={filtersAnalyticPanelState}
                            firstPageRef={pdfFirstPageRef}
                            secondPageRef={pdfSecondPageRef}
                        />
                    </>
                }
            </div>
        );
    }

    return (
        <div>
            <V2HeaderSection
                title={'Panel analityczny'}
                additionalElement={newAnalysisButton}
            />
            <FiltersContentWrapper
                className={clsx('offers-list', {
                    active: false
                })}
            >
                <Container id="filters">
                    <FiltersAnalyticPanel />
                </Container>
            </FiltersContentWrapper>
        </div>
    );
};

export default AnalyticPanelPage;
